import React, { useState } from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { contactInfo } from '../data/mock';
import { Heart, Calendar, Clock, Users, MessageCircle, Send, Sparkles, Wine, Gift, Star } from 'lucide-react';
import { useSEO } from '../utils/seo';

const ValentinePage = () => {
  useSEO('valentine');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    timeSlot: '',
    guests: '2',
    specialRequests: ''
  });

  const [errors, setErrors] = useState({});

  // Fixed date for Valentine's Day
  const valentineDate = 'Saturday, 14 February 2026';

  // Time slots from 10 AM to 1 AM
  const timeSlots = [
    '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
    '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
    '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
    '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
    '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM',
    '10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM',
    '12:00 AM', '12:30 AM', '1:00 AM'
  ];

  const packageInclusions = [
    { icon: Wine, text: 'Mocktail For 2' },
    { icon: Star, text: 'Sizzler (Any One)' },
    { icon: Gift, text: 'Brownie' },
    { icon: Heart, text: 'Poolside Table Decoration' }
  ];

  const termsAndConditions = [
    'Each booking is valid for a 45-minute session only.',
    'All slots must be booked in advance.',
    'If a guest arrives late, the session time will be counted from the booking slot time.'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Enter a valid 10-digit number';
    }
    if (!formData.timeSlot) newErrors.timeSlot = 'Select a time slot';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // WhatsApp message with plain text (no Unicode emojis for better compatibility)
    const message = `*Valentine's Day Booking Request*

*Package:* Poolside Romance (Rs.1999)

*Name:* ${formData.name}
*Phone:* ${formData.phone}
*Date:* ${valentineDate}
*Time Slot:* ${formData.timeSlot}
*Guests:* ${formData.guests}${formData.specialRequests ? `\n*Special Requests:* ${formData.specialRequests}` : ''}

Looking forward to celebrating Valentine's Day at White Valley!

https://whitevalleycafe.com/`;

    // Track conversion with Meta Pixel before opening WhatsApp
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead', {
        content_name: 'Valentine Booking',
        content_category: 'Reservation',
        value: 1999,
        currency: 'INR'
      });
    }

    const whatsappNumber = contactInfo.whatsapp.replace('+', '');
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="valentine-page">
      <style>{`
        .valentine-page {
          --valentine-primary: #c41e3a;
          --valentine-secondary: #e91e63;
          --valentine-accent: #ff6b9d;
          --valentine-dark: #8b1538;
          --valentine-cream: #fff8f5;
          min-height: 100vh;
          background: var(--valentine-cream);
        }

        /* Hero Banner Section */
        .valentine-banner {
          background: linear-gradient(135deg, var(--valentine-primary) 0%, var(--valentine-dark) 100%);
          padding: 100px 20px 40px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .valentine-banner::before {
          content: '';
          position: absolute;
          inset: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5c-2.5 0-4.5 1.5-5.5 3.5C23.5 6.5 21.5 5 19 5c-4 0-7 3-7 7 0 8 18 18 18 18s18-10 18-18c0-4-3-7-7-7-2.5 0-4.5 1.5-5.5 3.5C34.5 6.5 32.5 5 30 5z' fill='rgba(255,255,255,0.03)'/%3E%3C/svg%3E");
        }

        .banner-content {
          position: relative;
          z-index: 1;
          max-width: 800px;
          margin: 0 auto;
        }

        .banner-logo {
          font-family: 'Georgia', serif;
          font-size: 24px;
          color: rgba(255,255,255,0.9);
          margin-bottom: 16px;
        }

        .banner-title {
          font-family: 'Georgia', serif;
          font-size: 48px;
          color: white;
          margin: 0 0 12px;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
        }

        .banner-subtitle {
          font-size: 20px;
          color: rgba(255,255,255,0.9);
          margin: 0;
        }

        .floating-hearts {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
        }

        .float-heart {
          position: absolute;
          color: rgba(255,255,255,0.15);
          animation: floatUp 8s ease-in-out infinite;
        }

        @keyframes floatUp {
          0%, 100% { transform: translateY(100%) rotate(0deg); opacity: 0; }
          10% { opacity: 0.3; }
          90% { opacity: 0.3; }
          100% { transform: translateY(-100%) rotate(360deg); opacity: 0; }
        }

        /* Main Content Section */
        .valentine-main {
          max-width: 1200px;
          margin: 0 auto;
          padding: 60px 20px;
        }

        .valentine-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: start;
        }

        @media (max-width: 900px) {
          .valentine-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }
        }

        /* Left Side - Image & Package Info */
        .valentine-left {
          position: sticky;
          top: 100px;
        }

        .package-image-wrapper {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(196, 30, 58, 0.25);
          margin-bottom: 32px;
        }

        .package-image {
          width: 100%;
          height: 350px;
          object-fit: cover;
          display: block;
        }

        .package-badge {
          position: absolute;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, var(--valentine-primary), var(--valentine-secondary));
          color: white;
          padding: 12px 24px;
          border-radius: 30px;
          font-weight: 700;
          font-size: 18px;
          box-shadow: 0 8px 20px rgba(196, 30, 58, 0.4);
        }

        .package-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.08);
        }

        .package-title {
          font-size: 22px;
          font-weight: 700;
          color: var(--valentine-primary);
          margin: 0 0 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .package-list {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-bottom: 24px;
        }

        .package-item {
          display: flex;
          align-items: center;
          gap: 14px;
          font-size: 16px;
          color: #333;
        }

        .package-item-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, rgba(196, 30, 58, 0.1), rgba(233, 30, 99, 0.1));
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--valentine-primary);
        }

        .terms-section {
          background: rgba(196, 30, 58, 0.05);
          border-radius: 12px;
          padding: 16px;
        }

        .terms-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--valentine-primary);
          margin: 0 0 12px;
        }

        .terms-list {
          margin: 0;
          padding: 0 0 0 16px;
          font-size: 13px;
          color: #666;
          line-height: 1.6;
        }

        .terms-list li {
          margin-bottom: 6px;
        }

        /* Right Side - Booking Form */
        .valentine-right {
          background: white;
          border-radius: 24px;
          padding: 40px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.1);
          border: 1px solid rgba(196, 30, 58, 0.1);
        }

        .form-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .form-title {
          font-size: 28px;
          font-weight: 700;
          color: var(--valentine-primary);
          margin: 0 0 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
        }

        .form-subtitle {
          color: #666;
          font-size: 15px;
          margin: 0;
        }

        .form-divider {
          width: 60px;
          height: 3px;
          background: linear-gradient(90deg, var(--valentine-primary), var(--valentine-accent));
          border-radius: 2px;
          margin: 16px auto 0;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: #333;
          margin-bottom: 8px;
        }

        .form-label svg {
          color: var(--valentine-primary);
        }

        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid #eee;
          border-radius: 12px;
          font-size: 15px;
          transition: all 0.3s ease;
          background: #fafafa;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--valentine-primary);
          background: white;
          box-shadow: 0 0 0 4px rgba(196, 30, 58, 0.1);
        }

        .form-input.error,
        .form-select.error {
          border-color: #dc2626;
          background: #fef2f2;
        }

        .error-text {
          color: #dc2626;
          font-size: 12px;
          margin-top: 6px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        @media (max-width: 500px) {
          .form-row {
            grid-template-columns: 1fr;
          }
        }

        .form-textarea {
          resize: none;
          min-height: 80px;
        }

        .submit-btn {
          width: 100%;
          padding: 18px 32px;
          background: linear-gradient(135deg, var(--valentine-primary), var(--valentine-secondary));
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 18px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          transition: all 0.3s ease;
          box-shadow: 0 8px 30px rgba(196, 30, 58, 0.35);
          margin-top: 8px;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 40px rgba(196, 30, 58, 0.45);
        }

        .whatsapp-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 16px;
          padding: 12px;
          background: rgba(37, 211, 102, 0.1);
          border-radius: 10px;
          font-size: 13px;
          color: #128C7E;
        }

        @media (max-width: 900px) {
          .valentine-left {
            position: static;
          }
          .valentine-right {
            padding: 28px;
          }
          .banner-title {
            font-size: 36px;
          }
        }
      `}</style>

      <Header />

      {/* Hero Banner */}
      <section className="valentine-banner">
        <div className="floating-hearts">
          {[...Array(6)].map((_, i) => (
            <Heart
              key={i}
              className="float-heart"
              size={32}
              fill="currentColor"
              style={{
                left: `${15 + i * 15}%`,
                animationDelay: `${i * 1.2}s`
              }}
            />
          ))}
        </div>
        <div className="banner-content">
          <div className="banner-logo">White ❤ Valley</div>
          <h1 className="banner-title">Valentine's Day Special</h1>
          <p className="banner-subtitle">Celebrate Love by the Pool</p>
        </div>
      </section>

      {/* Main Content */}
      <main className="valentine-main">
        <div className="valentine-grid">
          {/* Left Side - Image & Package */}
          <div className="valentine-left">
            <div className="package-image-wrapper">
              <img
                src="/images/valentine-hero.png"
                alt="Romantic Poolside Dinner Setup"
                className="package-image"
              />
              <div className="package-badge">₹1999 / Table</div>
            </div>

            <div className="package-card">
              <h2 className="package-title">
                <Sparkles size={24} />
                Poolside Romance Package
              </h2>
              <div className="package-list">
                {packageInclusions.map((item, index) => (
                  <div key={index} className="package-item">
                    <div className="package-item-icon">
                      <item.icon size={20} />
                    </div>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
              <div className="terms-section">
                <h3 className="terms-title">Terms & Conditions</h3>
                <ul className="terms-list">
                  {termsAndConditions.map((term, index) => (
                    <li key={index}>{term}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Right Side - Booking Form */}
          <div className="valentine-right">
            <div className="form-header">
              <h2 className="form-title">
                <Heart size={28} fill="#c41e3a" />
                Book Your Table
              </h2>
              <p className="form-subtitle">Reserve your romantic evening now</p>
              <div className="form-divider"></div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    <Users size={16} />
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                    className={`form-input ${errors.name ? 'error' : ''}`}
                  />
                  {errors.name && <div className="error-text">{errors.name}</div>}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <MessageCircle size={16} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="10-digit number"
                    className={`form-input ${errors.phone ? 'error' : ''}`}
                  />
                  {errors.phone && <div className="error-text">{errors.phone}</div>}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Clock size={16} />
                  Time Slot
                </label>
                <select
                  name="timeSlot"
                  value={formData.timeSlot}
                  onChange={handleInputChange}
                  className={`form-select ${errors.timeSlot ? 'error' : ''}`}
                >
                  <option value="">Select time</option>
                  {timeSlots.map((slot, index) => (
                    <option key={index} value={slot}>{slot}</option>
                  ))}
                </select>
                {errors.timeSlot && <div className="error-text">{errors.timeSlot}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  <Sparkles size={16} />
                  Special Requests (Optional)
                </label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  placeholder="Any special arrangements, decorations, dietary preferences..."
                  className="form-textarea"
                />
              </div>

              <div style={{
                background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.08), rgba(233, 30, 99, 0.05))',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px',
                border: '1px solid rgba(196, 30, 58, 0.15)'
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#c41e3a', marginBottom: '8px' }}>
                  📌 Important Instructions
                </div>
                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#666', lineHeight: '1.7' }}>
                  <li>Date: <strong>Saturday, 14 February 2026</strong></li>
                  <li>Package valid for <strong>2 guests</strong> only</li>
                  <li>Each booking is for a <strong>45-minute session</strong></li>
                  <li>Advance booking required</li>
                </ul>
              </div>

              <button type="submit" className="submit-btn">
                <Send size={20} />
                Book Now
              </button>

              <div className="whatsapp-badge">
                <MessageCircle size={18} />
                Booking will be confirmed via WhatsApp
              </div>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ValentinePage;
